// NEW_FILE_JUSTIFIED: State management hook for template-driven pre-auth application wizard — mirrors useClaimApplicationWizard but for pre-authorizations

import { useState, useCallback } from 'react';
import { useMutation } from 'convex/react';
import { toast } from 'sonner';
import { api } from '@convex/_generated/api';
import type { Id } from '@convex/_generated/dataModel';
import type { IPreAuthApplicationData, TClaimantType } from '@/@types/claims';
import type { IFormStep } from '@/@types/policyFormTemplates';

// LOCAL_TYPE_JUSTIFIED: hook args interface co-located with the hook — not a component file
interface IUsePreAuthApplicationWizardArgs {
  templateSteps: IFormStep[];
  templateId: Id<'claimFormTemplates'>;
  orgId: Id<'organisations'>;
  policyId: Id<'insurancePolicies'>;
  existingPreAuthId?: Id<'preAuthorizations'>;
  existingData?: IPreAuthApplicationData;
  claimantType: TClaimantType;
  claimantId: string;
}

// LOCAL_TYPE_JUSTIFIED: hook return interface co-located with the hook — not a component file
interface IUsePreAuthApplicationWizardReturn {
  currentStep: number;
  values: Record<string, unknown>;
  goToStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  updateValue: (key: string, value: unknown) => void;
  saveDraft: () => Promise<void>;
  submitPreAuth: () => Promise<void>;
  isSaving: boolean;
  isSubmitting: boolean;
  preAuthId: Id<'preAuthorizations'> | null;
}

export function usePreAuthApplicationWizard({
  templateSteps,
  templateId,
  orgId,
  policyId,
  existingPreAuthId,
  existingData,
  claimantType,
  claimantId,
}: IUsePreAuthApplicationWizardArgs): IUsePreAuthApplicationWizardReturn {
  const [currentStep, setCurrentStep] = useState(existingData?.currentStep ?? 0);
  const [values, setValues] = useState<Record<string, unknown>>(existingData?.values ?? {});
  const [preAuthId, setPreAuthId] = useState<Id<'preAuthorizations'> | null>(
    existingPreAuthId ?? null
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const saveDraftMutation = useMutation(api.insurance.preAuthorizations.saveDraft);
  const updateDraftMutation = useMutation(api.insurance.preAuthorizations.updateDraft);
  const submitDraftMutation = useMutation(api.insurance.preAuthorizations.submitDraft);

  const totalSteps = templateSteps.length;

  const goToStep = useCallback(
    (step: number) => {
      setCurrentStep(Math.max(0, Math.min(step, totalSteps - 1)));
    },
    [totalSteps]
  );

  const nextStep = useCallback(() => {
    setCurrentStep(s => Math.min(s + 1, totalSteps - 1));
  }, [totalSteps]);

  const prevStep = useCallback(() => {
    setCurrentStep(s => Math.max(s - 1, 0));
  }, []);

  const updateValue = useCallback((key: string, value: unknown) => {
    setValues(prev => ({ ...prev, [key]: value }));
  }, []);

  const saveDraft = useCallback(async () => {
    setIsSaving(true);
    try {
      const applicationData: IPreAuthApplicationData = { currentStep, values };

      if (preAuthId) {
        await updateDraftMutation({
          preAuthId,
          preAuthApplicationData: applicationData,
          currentStep,
        });
        toast.success('Draft updated');
      } else {
        const newId = await saveDraftMutation({
          orgId,
          policyId,
          claimantType,
          claimantId,
          formTemplateId: templateId,
          preAuthApplicationData: applicationData,
          currentStep,
        });
        setPreAuthId(newId);
        toast.success('Draft saved');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save draft';
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  }, [
    currentStep,
    values,
    preAuthId,
    orgId,
    policyId,
    claimantType,
    claimantId,
    templateId,
    saveDraftMutation,
    updateDraftMutation,
  ]);

  const submitPreAuth = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const applicationData: IPreAuthApplicationData = { currentStep, values };

      if (preAuthId) {
        await updateDraftMutation({
          preAuthId,
          preAuthApplicationData: applicationData,
          currentStep,
        });
        await submitDraftMutation({ preAuthId });
      } else {
        const newId = await saveDraftMutation({
          orgId,
          policyId,
          claimantType,
          claimantId,
          formTemplateId: templateId,
          preAuthApplicationData: applicationData,
          currentStep,
        });
        setPreAuthId(newId);
        await submitDraftMutation({ preAuthId: newId });
      }
      toast.success('Pre-authorization submitted');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to submit pre-authorization';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    currentStep,
    values,
    preAuthId,
    orgId,
    policyId,
    claimantType,
    claimantId,
    templateId,
    saveDraftMutation,
    updateDraftMutation,
    submitDraftMutation,
  ]);

  return {
    currentStep,
    values,
    goToStep,
    nextStep,
    prevStep,
    updateValue,
    saveDraft,
    submitPreAuth,
    isSaving,
    isSubmitting,
    preAuthId,
  };
}
